import { unwrapResult } from '@reduxjs/toolkit';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

/** Components */
import Modal from '../../../../../components/Modal';
import Form from './From';

/** Hooks */
import useCustomers from '../../../../../hooks/useCustomers';

/** Types */

import {
  CustomerBase,
  validationSchema,
} from '../../../../../pages/definitions/Customers/types';
import { isApiError } from '../../../../../services/api/types';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm?: (name: string) => void;
  item?: CustomerBase | null;
  callerPhone?: string | null;
  setCustomerId: (value: ((prevState: string) => string) | string) => void;
}

function CustomerGroupAddModal({
  open,
  onClose,
  onConfirm,
  item,
  callerPhone,
  setCustomerId,
}: Props) {
  const { t } = useTranslation('customer');
  const { enqueueSnackbar } = useSnackbar();
  const { addCustomer, updateCustomer } = useCustomers();

  const initialValues = {
    id: '',
    first_name: item?.first_name || '',
    last_name: item?.last_name || '',
    phone_number: callerPhone || item?.phone_number || '',
    is_active: item?.is_active || false,
    is_open_account: item?.is_open_account || false,
    phone_number_country: item?.phone_number_country || 'TR',
    chargeable_amount: null,
  };

  const { handleSubmit, control, reset } = useForm<CustomerBase>({
    defaultValues: initialValues,
    resolver: validationSchema,
  });

  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');
  const [info, setInfo] = useState<any>(null);

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleChange = (newValue: any, info: any) => {
    setValue(newValue);
    setInfo(info);
  };

  useEffect(() => {
    reset(initialValues);
    if (item) {
      setValue(item.phone_number);
    } else {
      setValue('');
    }

    if (callerPhone) {
      setValue(callerPhone);
    }
  }, [open]);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await handleSubmit(async values => {
        try {
          if (item) {
            await updateCustomer({
              ...values,
              id: item.id,
              phone_number_country:
                info?.countryCode || item.phone_number_country,
              phone_number: info?.nationalNumber || item.phone_number,
            });
          } else {
            await addCustomer({
              ...values,
              phone_number_country:
                info?.countryCode || values.phone_number_country,
              phone_number: info?.nationalNumber || values.phone_number,
              chargeable_amount: values.chargeable_amount
                ? Number(values.chargeable_amount)
                : 0,
            });
          }
          // Eğer bu noktaya ulaşırsak istek başarılı demektir
          if (info?.nationalNumber || values.phone_number) {
            onClose();
          }
        } catch (err) {
          // İstek başarısız olduğunda hata mesajını göster
          enqueueSnackbar(
            isApiError(err) ? err.message : t('common:unknownError' as any),
            {
              variant: 'error',
              style: { whiteSpace: 'pre-line' },
            },
          );
          throw err; // Hata fırlatılıyor, dıştaki catch bunu yakalayacak
        }
      })();
    } catch (error) {
      // Ek hata işlemleri yapılabilir
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('actions.addCustomer')}
      titleIconName="Add"
      loading={loading}
      open={open}
      onClose={onClose}
      negativeAction={t('actions.close')}
      positiveAction={t('actions.add')}
      onPositiveClick={handleConfirm}
      onNegativeClick={onClose}
    >
      <Form
        item={item}
        control={control}
        value={value}
        handleChange={handleChange}
      />
    </Modal>
  );
}

export default CustomerGroupAddModal;
